import { useParams } from 'react-router-dom';
import { useAppContext } from '@/Main';
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import { decodeGetter } from '@/app/common/components/ag-grid/configs';
import { getColor, isFirstRow, isLastRow } from '@/helpers/utils';

import { MdOutlineOpenInNew } from 'react-icons/md';
import DomainRankCellRenderer from './DomainRankCellRenderer';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import SearchVolumeChart from '@/app/common/components/CellRenderers/SearchVolumeChart';
import { errorMessage } from '@/utils/toast';
import type { ColDef, ColumnMenuTab, ICellRendererParams } from 'ag-grid-community';
import type { SerpItem } from '@/app/Serp/types/serpApiTypes';
import { serpTooltips } from '@/app/Serp/tooltip/serpTooltips';

type BaseColumn<T> = ColDef<T> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  noFilter?: boolean;
  tooltip?: string;
};

export type RelatedOrSuggestedRow = {
  keyword: string;
  search_volume_miztols?: number;
  search_volume_dates?: (string | number)[];
  search_volume_values?: number[];
  type?: number;
  length?: number;
  [key: string]: unknown;
};

export type WordDensityRow = {
  word: string;
  num_repetitions?: number;
  density?: number;
  num_of_words?: number;
  [key: string]: unknown;
};

export type AltTagRow = {
  alt: string;
  repetitions?: number;
  [key: string]: unknown;
};

export type SeoTitleRow = {
  seo_title: string;
  seo_title_len?: number;
  page_title?: string;
  page_title_len?: number;
  url?: string;
  rank?: number;
  [key: string]: unknown;
};

export type HtagRow = {
  title: string;
  type?: number;
  url?: string;
  [key: string]: unknown;
};

const menuTabs: ColumnMenuTab[] = [
  'columnsMenuTab',
  'filterMenuTab',
  'generalMenuTab'
];
export const relatedOrSuggested: BaseColumn<RelatedOrSuggestedRow>[] = [
  {
    searchable: true,
    field: 'keyword',
    type: 'string',
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterValueGetter: decodeGetter,
    title: 'کلمه',
    width: 100,
    flex: 1
  },

  {
    searchable: true,
    field: 'search_volume_miztols',
    type: 'number',
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    title: 'میزان جستجو',
    width: 130
  },
  {
    field: 'search_volume_dates',
    type: 'array',
    title: 'روند جستجو',
    width: 120,
    cellStyle: {
      textAlign: 'center',
      margin: '0px'
    },
    sortable: false,
    cellClass: 'over-flow',
    noFilter: true,
    searchable: false,
    cellRenderer: (params: ICellRendererParams<RelatedOrSuggestedRow>) => {
      return params.value ? (
        <SearchVolumeChart
          isFirstRow={isFirstRow(params)}
          isLastRow={isLastRow(params)}
          count={22}
          dates={params.value}
          series={[
            {
              name: 'روند جستجو',
              data: (params.data?.search_volume_values ?? []).map(
                (value, i) => ({
                  name: (params.data?.search_volume_dates ?? [])[i],
                  value: value,
                  itemStyle: {
                    color: getColor('navbar-vertical-default-link-active-color')
                  }
                })
              )
            }
          ]}
        />
      ) : (
        ''
      );
    }
  },
  {
    searchable: true,
    field: 'type',
    type: 'string',
    cellStyle: {
      textAlign: 'center',
      margin: '0px'
    },
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    title: 'مرتبط یا پیشنهادی',
    minWidth: 100,
    filterValueGetter: (params: { data?: RelatedOrSuggestedRow }) =>
      params.data?.type && params.data.type === 1 ? 'مرتبط' : 'پیشنهادی',
    cellRenderer: (params: ICellRendererParams<RelatedOrSuggestedRow>) => {
      return params.value ? (params.value === 1 ? 'مرتبط' : 'پیشنهادی') : '';
    }
  },
  // {
  //   searchable: true,
  //   field: 'importance',
  //   type: 'number',
  //   menuTabs,
  //   filter: 'agTextColumnFilter',
  //   floatingFilter: true,
  //   title: 'میزان اهمیت',
  //   width: 120
  // },
  {
    searchable: true,
    field: 'length',
    type: 'number',
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    title: 'طول',
    width: 90
  }
];
export const contentWebsite = (
  isMdUp?: boolean
): BaseColumn<SerpItem>[] => {
  const defCols: BaseColumn<SerpItem>[] = [
    {
      searchable: true,
      field: 'rank_absolute',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'رتبه',
      width: 73
    },
    {
      searchable: true,
      field: 'title',
      type: 'string',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterValueGetter: decodeGetter,
      title: 'عنوان سئو',
      minWidth: 170,
      flex: 1
    },
    {
      searchable: true,
      field: 'domain_rank',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'DR (قدرت دامنه)',
      tooltip: serpTooltips.domainRank,
      width: 160,
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.data?.type === 'organic' ||
        params.data?.type === 'feature_snippet' ? (
          params.value !== -10 ? (
            <DomainRankCellRenderer num={params.value} />
          ) : (
            <TextLoading />
          )
        ) : (
          ''
        )
    },
    {
      searchable: true,
      field: 'num_words',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد کلمات',
      width: 120,
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.value !== -1 ? params.value : <TextLoading />
    },
    {
      searchable: true,
      field: 'num_unique_words',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد کلمات یکتا',
      width: 140,
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.value !== -1 ? params.value : <TextLoading />
    },
    {
      searchable: true,
      field: 'num_sentences',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد جملات',
      width: 120,
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.value !== -1 ? params.value : <TextLoading />
    },
    {
      searchable: true,
      field: 'num_paragraphs',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد پاراگراف',
      width: 130,
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.value !== -1 ? params.value : <TextLoading />
    },
    {
      searchable: true,
      field: 'study_duration',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'میانگین زمان مطالعه',
      width: 155,
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.value !== -1 ? params.value : <TextLoading />
    },
    {
      searchable: true,
      field: 'num_h1',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد H1',
      width: 100,
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.value !== -1 ? params.value : <TextLoading />
    },
    {
      searchable: true,
      field: 'num_h2',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد H2',
      width: 100,
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.value !== -1 ? params.value : <TextLoading />
    },
    {
      searchable: true,
      field: 'num_h3',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد H3',
      width: 100,
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.value !== -1 ? params.value : <TextLoading />
    },
    {
      searchable: true,
      field: 'num_images',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد تصاویر',
      width: 120,
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.value !== -1 ? params.value : <TextLoading />
    }
  ];
  const urlCol: BaseColumn<SerpItem> = {
    searchable: true,
    field: 'url',
    type: 'string',
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterValueGetter: decodeGetter,
    title: 'نشانی',
    pinned: isMdUp ? 'left' : undefined,
    cellRenderer: (params: ICellRendererParams<SerpItem>) => (
      <URLRenderer url={params.value} />
    )
  };
  const copyCols = [...defCols];
  isMdUp ? copyCols.push(urlCol) : copyCols.unshift(urlCol);
  return copyCols;
};
export const density: BaseColumn<WordDensityRow>[] = [
  {
    searchable: true,
    field: 'word',
    type: 'string',
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterValueGetter: decodeGetter,
    title: 'کلمات عناوین سئو',
    minWidth: 145,
    flex: 1
  },
  {
    searchable: true,
    field: 'num_repetitions',
    type: 'number',
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    title: 'تعداد تکرار',
    width: 120
  },
  {
    searchable: true,
    field: 'density',
    type: 'number',
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    title: 'چگالی',
    width: 90
  },
  {
    searchable: true,
    field: 'num_of_words',
    type: 'number',
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    title: 'تعداد کلمات',
    width: 120
  }
];
export const altTags: BaseColumn<AltTagRow>[] = [
  {
    searchable: true,
    field: 'alt',
    type: 'string',
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterValueGetter: decodeGetter,
    title: 'متن جایگزین تصویر',
    minWidth: 170,
    flex: 1,
    cellRenderer: (
      params: ICellRendererParams<AltTagRow> & { keyword?: string }
    ) => {
      const { keyword: routeKeyword } = useParams<{ keyword?: string }>();
      const keyword = keywordFromCell(params, routeKeyword);
      return (
        params.value && (
          <KeywordHighlightText
            value={String(params.value)}
            keyword={keyword}
          />
        )
      );
    }
  },
  {
    searchable: true,
    field: 'repetitions',
    type: 'number',
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    title: 'تعداد تکرار',
    width: 120
  }
];
const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const decodeText = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const keywordTokens = (keyword: string) => {
  const unique = new Set<string>();
  decodeText(keyword)
    .trim()
    .split(/[\s\u200c]+/)
    .forEach(word => {
      const token = word.trim();
      if (token) unique.add(token);
    });
  return [...unique].sort((a, b) => b.length - a.length);
};

const flexibleToken = (word: string) =>
  [...word]
    .map(char => {
      if (char === 'ی' || char === 'ي') return '[یي]';
      if (char === 'ک' || char === 'ك') return '[کك]';
      return escapeRegExp(char);
    })
    .join('');

const highlightParts = (raw: string, keyword?: string) => {
  const text = decodeText(raw).replace(/<[^>]*>/g, '');
  const tokens =
    keyword && keyword !== 'last' ? keywordTokens(keyword) : [];
  if (!tokens.length) return [{ text, match: false }];

  const re = new RegExp(tokens.map(flexibleToken).join('|'), 'giu');
  const parts: { text: string; match: boolean }[] = [];
  let last = 0;
  for (const match of text.matchAll(re)) {
    const index = match.index ?? 0;
    if (index > last) {
      parts.push({ text: text.slice(last, index), match: false });
    }
    if (match[0]) parts.push({ text: match[0], match: true });
    last = index + match[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last), match: false });
  return parts.length ? parts : [{ text, match: false }];
};

const KeywordHighlightText = ({
  value,
  keyword
}: {
  value?: string;
  keyword?: string;
}) => {
  const {
    config: { isDark }
  } = useAppContext() as { config: { isDark: boolean } };
  const parts = value ? highlightParts(value, keyword) : [];
  const highlightColor = isDark
    ? getColor('ourcolor-2')
    : getColor('ourcolor-1');

  return (
    <span>
      {parts.map((part, index) =>
        part.match ? (
          <span
            key={index}
            className="highlight"
            style={{
              backgroundColor: highlightColor,
              padding: '2px 3px 0'
            }}
          >
            {part.text}
          </span>
        ) : (
          <span key={index}>{part.text}</span>
        )
      )}
    </span>
  );
};

const keywordFromCell = (
  params: {
    keyword?: string;
    colDef?: { cellRendererParams?: { keyword?: string } };
  },
  routeKeyword?: string
) => params.keyword || params.colDef?.cellRendererParams?.keyword || routeKeyword;

export const seoTitles: {
  first: BaseColumn<SeoTitleRow>[];
  second: BaseColumn<WordDensityRow>[];
} = {
  first: [
    {
      searchable: true,
      field: 'rank',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'رتبه',
      width: 73,
      minWidth: 73,
      maxWidth: 90
    },
    {
      searchable: true,
      field: 'seo_title',
      cellStyle: {
        textAlign: 'center',
        margin: '0px'
        // display: 'flex',
        // alignItems: 'center',
        // gap: '5px'
      },
      //   cellClass: 'over-flow',
      type: 'string',
      filterValueGetter: decodeGetter,
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'عناوین سئو سایت های برتر',
      minWidth: 280,
      flex: 3,
      cellRenderer: (
        params: ICellRendererParams<SeoTitleRow> & { keyword?: string }
      ) => {
        const { keyword: routeKeyword } = useParams<{ keyword?: string }>();
        const keyword = keywordFromCell(params, routeKeyword);
        return (
          params.value && (
            <div className="d-flex align-items-center gap-1">
              <MdOutlineOpenInNew
                title="بازکردن پیوند"
                onClick={() => {
                  try {
                    window.open(decodeURI(params.data?.url ?? ''), '_blank');
                  } catch (error) {
                    // Handle the error here
                    errorMessage('.پیوند قابل بازکردن نمی‌باشد');
                    console.error('خطا در بازکردن پیوند:', error);
                  }
                }}
                className="cursor-pointer little-icon"
                style={{ paddingLeft: '3px' }}
                size={18}
              />
              <KeywordHighlightText
                value={String(params.value)}
                keyword={keyword}
              />
            </div>
          )
        );
      }
    },
    {
      searchable: true,
      field: 'seo_title_len',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'طول عنوان سئو',
      minWidth: 130,
      flex: 1
    },
    {
      searchable: true,
      field: 'page_title',
      type: 'string',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterValueGetter: decodeGetter,

      title: 'عنوان صفحه',
      minWidth: 200,
      flex: 2
    },
    {
      searchable: true,
      field: 'page_title_len',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'طول عنوان صفحه',
      minWidth: 140,
      flex: 1
    }
  ],
  second: [
    {
      searchable: true,
      field: 'word',
      type: 'string',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterValueGetter: decodeGetter,
      title: 'کلمات عناوین سئو',
      width: 145,
      flex: 1
    },
    {
      searchable: true,
      field: 'num_repetitions',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد تکرار',
      width: 120
    },
    {
      searchable: true,
      field: 'density',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'چگالی',
      width: 90
    },
    {
      searchable: true,
      field: 'num_of_words',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد کلمات',
      width: 120
    }
  ]
};
export const htags: {
  first: BaseColumn<HtagRow>[];
  second: BaseColumn<WordDensityRow>[];
} = {
  first: [
    {
      cellStyle: {
        textAlign: 'center',
        margin: '0px'
        // display: 'flex',
        // alignItems: 'center',
        // gap: '5px'
      },
      searchable: true,
      field: 'title',
      type: 'string',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterValueGetter: decodeGetter,
      title: 'عنوان صفحه',
      minWidth: 170,
      flex: 1,
      cellRenderer: (
        params: ICellRendererParams<HtagRow> & { keyword?: string }
      ) => {
        const { keyword: routeKeyword } = useParams<{ keyword?: string }>();
        const keyword = keywordFromCell(params, routeKeyword);
        return (
          params.value && (
            <div className="d-flex align-items-center gap-1">
              <MdOutlineOpenInNew
                title="بازکردن پیوند"
                onClick={() => {
                  try {
                    window.open(decodeURI(params.data?.url ?? ''), '_blank');
                  } catch (error) {
                    // Handle the error here
                    errorMessage('.پیوند قابل بازکردن نمی‌باشد');
                    console.error('خطا در بازکردن پیوند:', error);
                  }
                }}
                className="cursor-pointer little-icon"
                style={{ paddingLeft: '3px' }}
                size={18}
              />
              <KeywordHighlightText
                value={String(params.value)}
                keyword={keyword}
              />
            </div>
          )
        );
      }
    },
    {
      searchable: true,
      field: 'type',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'نوع تگ',
      filterValueGetter: (params: { data?: HtagRow }) =>
        params.data?.type && 'h' + params.data.type,
      width: 84,
      minWidth: 84,
      maxWidth: 96,
      cellStyle: {
        textAlign: 'center',
        margin: '0px'
      },
      cellRenderer: (params: ICellRendererParams<HtagRow>) =>
        params.value && 'H' + params.value
    }
  ],
  second: [
    {
      searchable: true,
      field: 'word',
      type: 'string',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterValueGetter: decodeGetter,
      title: 'کلمه',
      width: 100,
      flex: 1
    },
    {
      searchable: true,
      field: 'num_repetitions',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد تکرار',
      width: 120
    },
    {
      searchable: true,
      field: 'density',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'چگالی',
      width: 90
    },
    {
      searchable: true,
      field: 'num_of_words',
      type: 'number',
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      title: 'تعداد کلمات',
      width: 120
    }
  ]
};
